<template>
  <div>
    <div class="md-layout">
      <div class="md-layout-item md-size-100 mx-auto">
        <md-card>
          <md-card-header class="md-card-header-icon md-card-header-blue">
            <graph-tool-bar :dateRangeMenu="false">
              <template slot="leftSide">
                <maps-hierarchy-menu ref="menu" @on-selected="onMenuSelected"></maps-hierarchy-menu>
              </template>
            </graph-tool-bar>
          </md-card-header>

          <md-card-content>
            <md-empty-state
              v-if="!imageFile && !loading"
              md-icon="map"
              :md-label="$lang.titles.upload_new_map"
              :md-description="$lang.messages.select_image_and_upload_map"
            >
              <md-button class="md-primary md-raised md-fileinput">
                <template>{{$lang.keys.select_image}}</template>
                <input type="file" @change="onImageSelected" accept="image/*" />
              </md-button>
            </md-empty-state>

            <div
              class="scrolling-wrapper"
              v-if="imageFile"
              ref="contentArea"
              @scroll="onResponsive"
            >
              <img ref="buttonArea" :src="imageFile" @load="onMapLoad" />

              <VueDragResize
                v-for="(button, key) in buttonsList"
                :key="key"
                v-if="!loading"
                :parentLimitation="true"
                :isActive="editMode"
                :preventActiveBehavior="!editMode"
                :isResizable="false"
                :isDraggable="editMode"
                :parentW="contentAreaWidth + 40"
                :parentH="buttonAreaHeight"
                :h="50"
                :x="button.position_x"
                :y="button.position_y"
                @dragstop="onDragstop($event, button)"
                v-bind:class="{ 'hide-button': button.position_x < 20 || button.position_x > contentAreaWidth - 60}"
                style="width: 100px;"
              >
                <div>
                  <md-button
                    class="md-icon-button md-raised md-dense md-round delete-icon"
                    v-if="editMode"
                    title="Delete"
                    @click="deleteButton($event, button)"
                  >
                    <md-icon>close</md-icon>
                  </md-button>

                  <md-button
                    class="md-block"
                    :class="{'md-success':(button.status == true && deviceStatus(button.device)), 'md-danger':(button.status == false && deviceStatus(button.device))}"
                    :title="button.name"
                    :disabled="!deviceStatus(button.device)"
                    @click="setRelay(button)"
                  >{{buttonNameLimiter(button.name)}}</md-button>
                </div>
              </VueDragResize>
            </div>
          </md-card-content>
        </md-card>
      </div>

      <right-bar
        :devices="disconnectorList"
        :editMode="editMode"
        @update:editable="onUpdateEditableMode"
        @on-new-button="onCreatedButton"
        @on-delete="removeImage"
        @on-change="onImageSelected"
      ></right-bar>
      <snack-bar ref="snackbar"></snack-bar>
    </div>
  </div>
</template>


<script>
import GraphToolBar from "@/components/GraphToolBar";
import MapsHierarchyMenu from "./components/MapsMenu";
import RightBar from "./components/RightBar";
import snackBar from "@/components/SnackBar";

import Vue from "vue";
import axios from "axios";
import { getSelectedDeviceToken, getUserToken } from "@/storage/memoryStorage";
import { SlideYUpTransition } from "vue2-transitions";
import VueDragResize from "vue-drag-resize";

import Swal from "sweetalert2";

export default {
  components: {
    GraphToolBar,
    snackBar,
    MapsHierarchyMenu,
    RightBar,
    SlideYUpTransition,
    VueDragResize
  },

  data() {
    return {
      editMode: false,
      buttonsList: [],
      disconnectorList: [],
      buttonAreaWidth: 1,
      buttonAreaHeight: 1,
      contentAreaWidth: 1,
      offset: 0,
      scrollLeft: 0,
      imageFile: null,
      selected: null,
      disconnectorsSubscribed: [],
      loading: true
    };
  },

  computed: {
    WSPayload() {
      return this.$store.getters.getPayload;
    },
    isConnected() {
      return this.$store.getters.isConnected;
    },
    deviceList() {
      return this.$store.getters.getDeviceList;
    }
  },

  watch: {
    WSPayload: function(payload, oldPayload) {
      if (payload.topic === "Data") {
        for (let index = 1; index <= 4; index++) {
          const buttonListIndex = this.buttonsList.findIndex(
            btn => btn.device === payload.device && btn.relay === index
          );
          if (buttonListIndex !== -1) {
            this.buttonsList[buttonListIndex].status =
              payload.data["relay_" + index];
          }
        }
      }
    },
    isConnected: function(newStatus, oldStatus) {
      if (newStatus) {
        this.disconnectorsSubscribed = [];
        this.handleSubscribe();
      }
    }
  },

  mounted() {
    window.addEventListener("resize", this.onResponsive);
  },

  methods: {
    onMenuSelected(value) {
      if (value.ID_map) {
        this.buttonsList = value.buttonList;
        if (this.selected) {
          if (this.selected.ID_map !== value.ID_map) {
            this.getImage(value.ID_map);
          }
        } else {
          this.getImage(value.ID_map);
        }
      } else {
        this.loading = false;
        this.imageFile = null;
        this.buttonsList = [];
      }
      this.selected = value;
    },

    onMapLoad() {
      this.loading = false;
      Swal.close();
      this.createDisconnectorList();
      this.handleSubscribe();
      this.onResponsive();
    },

    getImage(mapID) {
      this.loading = true;
      Swal.fire({
        title: this.$lang.titles.loading,
        text: this.$lang.messages.please_wait,
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false
      });

      axios({
        method: "POST",
        url: this.$ENDPOINTS.API_R + "/applications/controller/map",
        data: {
          user_token: getUserToken(),
          ID_map: mapID
        }
      })
        .then(response => {
          this.imageFile = response.data.data;
        })
        .catch(error => {
          Swal.close();
          this.loading = false;
          this.imageFile = null;
          if (error.response.data.code !== 610) {
            this.$refs.snackbar.notify(
              this.$lang.messages.loading_image_failed
            );
          }
        });
    },

    onImageSelected(event) {
      const files = event.target.files || event.dataTransfer.files;
      if (!files.length) return;

      const allowedImageTypes = ["image/png", "image/jpeg", "image/jpg"];
      if (!allowedImageTypes.includes(files[0].type)) {
        this.$refs.snackbar.notify(
          this.$lang.messages.file_format_not_supported
        );
        return;
      }

      //MAX 1MB
      if (files[0].size > 1048576) {
        this.$refs.snackbar.notify(this.$lang.messages.file_too_large);
        return;
      }

      Swal.fire({
        title: this.$lang.titles.uploading,
        text: this.$lang.messages.please_wait,
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false
      });

      var reader = new FileReader();
      reader.onload = e => {
        var image = new Image();
        image.src = e.target.result;

        const formData = new FormData();
        formData.append("user_token", getUserToken());
        if (this.selected.ID_map) {
          formData.append("ID_map", this.selected.ID_map);
        }
        formData.append("image", files[0]);

        axios
          .post(
            this.$ENDPOINTS.API_W + "/applications/controller/map",
            formData
          )
          .then(res => {
            this.imageFile = e.target.result;

            this.$refs.menu.setMap({
              ID: res.data.data.ID_map
            });

            Swal.close();
          })
          .catch(err => {
            Swal.fire({
              title: this.$lang.messages.error,
              text: this.$lang.messages.image_file_upload_failed,
              type: "error",
              confirmButtonClass: "md-button md-warning",
              confirmButtonText: this.$lang.keys.close,
              buttonsStyling: false
            });
            this.imageFile = null;
            return;
          });
      };
      reader.readAsDataURL(files[0]);
    },

    removeImage(type) {
      this.imageFile = null;
    },

    onUpdateEditableMode(editable) {
      this.editMode = editable;
    },

    onDragstop(position, btn) {
      btn.position_x = position.left;
      btn.position_y = position.top;

      btn.original_x = position.left - this.offset + this.scrollLeft;
      btn.original_y = position.top;

      this.$refs.menu.setButtonsList(this.buttonsList);
    },

    onCreatedButton(btn) {
      let yscrollOffset = window.scrollY;
      if (yscrollOffset > 132) {
        yscrollOffset = yscrollOffset - 132;
      }

      this.buttonsList.push({
        name: btn.name,
        position_x: 100,
        original_x: 100,
        original_y: yscrollOffset + 100,
        position_y: yscrollOffset + 100,
        device: btn.deviceMAC,
        relay: btn.relay,
        status: null
      });

      this.disconnectorList.forEach((disconnector, index) => {
        if (disconnector.name === btn.deviceMAC) {
          this.disconnectorList[index].relays = this.disconnectorList[
            index
          ].relays.filter(el => el.value !== btn.relay);
        }
      });

      this.handleSubscribe();
      this.$refs.menu.setButtonsList(this.buttonsList);
    },

    deleteButton(el, delBtn) {
      this.buttonsList = this.buttonsList.filter(
        list => list.device !== delBtn.device || list.relay !== delBtn.relay
      );

      const disconnectorListIndex = this.disconnectorList.findIndex(
        el => el.deviceMAC === delBtn.device
      );
      if (disconnectorListIndex !== -1) {
        this.disconnectorList[disconnectorListIndex].relays.push({
          name: "(" + delBtn.relay + ") " + delBtn.name,
          value: Number(delBtn.relay)
        });

        this.disconnectorList[disconnectorListIndex].relays.sort((a, b) => {
          if (a.value > b.value) {
            return 1;
          } else if (a.value < b.value) {
            return -1;
          }
          return 0;
        });
      }

      this.$refs.menu.setButtonsList(this.buttonsList);
    },

    setRelay(btn) {
      if (!this.editMode) {
        if (btn.status !== null) {
          let data = {
            token: getSelectedDeviceToken(btn.device)
          };
          data["relay_" + btn.relay] = !btn.status;

          axios({
            method: "POST",
            url: this.$ENDPOINTS.API_W + "/disconnector/set-relay",
            data: data
          })
            .then(response => {})
            .catch(error => {
              if (error.response) {
                this.$refs.snackbar.notify(
                  this.$lang.messages.api_communication_error
                );
              }
            });
        }
      }
    },

    getRelayNames(deviceMAC) {
      return axios({
        method: "POST",
        url: this.$ENDPOINTS.API_R + "/disconnector/settings",
        data: {
          token: getSelectedDeviceToken(deviceMAC),
          names: true
        },
        withCredentials: true
      })
        .then(response => {
          return response.data.data;
        })
        .catch(error => {
          if (error.response) {
            if (error.response.data.code == 610) {
              return {
                R1: {
                  name: this.$lang.charts.relay + " 1"
                },
                R2: {
                  name: this.$lang.charts.relay + " 2"
                },
                R3: {
                  name: this.$lang.charts.relay + " 3"
                },
                R4: {
                  name: this.$lang.charts.relay + " 4"
                }
              };
            } else {
              this.$refs.snackbar.notify(
                this.$lang.messages.api_communication_error
              );
            }
          }
        });
    },

    createDisconnectorList() {
      this.disconnectorList = [];

      this.deviceList.forEach(device => {
        if (device.type === 2) {
          this.getRelayNames(device.mac).then(relaysName => {
            let allRelays = [
              {
                name: "(1) " + relaysName.R1.name,
                value: 1
              },
              {
                name: "(2) " + relaysName.R2.name,
                value: 2
              },
              {
                name: "(3) " + relaysName.R3.name,
                value: 3
              },
              {
                name: "(4) " + relaysName.R4.name,
                value: 4
              }
            ];

            this.buttonsList.forEach(button => {
              if (button.device === device.mac) {
                allRelays = allRelays.filter(
                  relay => relay.value !== button.relay
                );
              }
              button.status = null;
            });

            this.disconnectorList.push({
              name: device.mac,
              deviceMAC: device.mac,
              relays: allRelays
            });
          });
        }
      });
    },

    onResponsive() {
      if (this.$refs.buttonArea) {
        this.buttonAreaWidth = this.$refs.buttonArea.clientWidth;
        this.buttonAreaHeight = this.$refs.buttonArea.clientHeight;

        this.contentAreaWidth = this.$refs.contentArea.clientWidth;
        this.offset = this.$refs.buttonArea.offsetLeft - 20;
        this.scrollLeft = this.$refs.contentArea.scrollLeft;

        this.$nextTick(() => {
          this.buttonsList.forEach((btn, index) => {
            Vue.set(
              this.buttonsList[index],
              "position_x",
              btn.original_x + this.offset - this.scrollLeft
            );
          });
        });
      }
    },

    handleSubscribe() {
      this.buttonsList.forEach(button => {
        const disconnectorIndex = this.disconnectorsSubscribed.findIndex(
          el => el.device === button.device
        );

        if (disconnectorIndex === -1) {
          if (this.isConnected) {
            const wsActionMessage = {
              deviceToken: getSelectedDeviceToken(button.device),
              data: true
            };
            this.$socket.send(JSON.stringify(wsActionMessage));
            this.disconnectorsSubscribed.push(button);
          }
        }
      });
    },

    deviceStatus(deviceMAC) {
      if (!this.isConnected) {
        return 0;
      }
      try {
        const deviceListIndex = this.deviceList.findIndex(
          e => e.mac == deviceMAC
        );
        if (this.deviceList[deviceListIndex].status === "ONLINE") {
          return 1;
        } else {
          return 0;
        }
      } catch (err) {
        return 0;
      }
    },

    buttonNameLimiter(title) {
      if (title.length > 13) {
        return title.slice(0, 13) + "...";
      }
      return title;
    }
  },

  beforeDestroy() {
    // try {
    //   this.$refs.menu.setButtonsList(this.buttonsList);
    // } catch {}
    window.removeEventListener("resize", this.onResponsive);

    if (this.isConnected) {
      this.disconnectorsSubscribed.forEach(button => {
        const wsActionMessage = {
          deviceToken: getSelectedDeviceToken(button.device),
          data: false
        };
        this.$socket.send(JSON.stringify(wsActionMessage));
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.md-layout {
  margin-top: -29px;
}

.md-icon-button.md-dense {
  width: 21px;
  min-width: 21px;
  height: 21px;
}

.delete-icon {
  position: absolute;
  z-index: 1;
  top: -12px;
  left: 88px;
}

.md-card img {
  width: auto;
  max-width: 1500px;
  height: auto;
  display: block;
  margin-left: auto;
  margin-right: auto;
}

.scrolling-wrapper {
  overflow-x: scroll;
  overflow-y: hidden;
  white-space: nowrap;
}

.hide-button {
  display: none;
}
</style>